import Cookies from "js-cookie";
import userHttp from "../axios.services/http/instances/userHttp";
// type AuthPayload = {
//   aut?: string;
//   sid?: string;
//   expiry?: string;
//   uid: string;
//   role: string;
// };

// const COOKIE_TOKEN = "bbps_aut";
// const COOKIE_SESSION = "bbps_sid";
// const BROADCAST_CHANNEL = "bbps-auth";

type AuthPayload = {
  token?: string;
  sessionId?: string;
  expiresAtUtc?: string;
};

const COOKIE_TOKEN = "bbps_token";
const COOKIE_SESSION = "bbps_sessionid";
const BROADCAST_CHANNEL = "bbps-auth";

class SessionManagerClass {
  private accessToken: string | null = null; // in-memory token
  private sessionId: string | null = null; // in-memory sessionId
  private expiresAtUtc: string | null = null; // in-memory expiry
  private refreshingPromise: Promise<void> | null = null; // single refresh queue
  private bc: BroadcastChannel | null = null; // Broadcast channel if available

  constructor() {
    // Initialize from cookies (so reload is covered)
    this.accessToken = Cookies.get(COOKIE_TOKEN) || null;
    this.sessionId = Cookies.get(COOKIE_SESSION) || null;

    // Setup BroadcastChannel if browser supports it
    if (typeof window !== "undefined" && "BroadcastChannel" in window) {
      this.bc = new BroadcastChannel(BROADCAST_CHANNEL);
      this.bc.onmessage = (ev) => {
        const { type, payload } = ev.data || {};
        if (type === "SET_TOKEN") {
          // when another tab sets token -> update in-memory copy
          this.accessToken = payload?.token ?? null;
          this.sessionId = payload?.sessionId ?? null;
          this.expiresAtUtc = payload?.expiresAtUtc ?? null;
        }
        if (type === "CLEAR") {
          // when another tab logs out -> clear local cache
          this.clearLocalOnly();
        }
      };
    }

    // fallback: storage event for older browsers
    window.addEventListener("storage", (e) => {
      if (e.key === "bbps-auth") {
        try {
          const msg = JSON.parse(String(e.newValue));
          if (msg?.type === "SET_TOKEN") {
            this.accessToken = msg.payload?.token ?? null;
            this.sessionId = msg.payload?.sessionId ?? null;
            this.expiresAtUtc = msg.payload?.expiresAtUtc ?? null;
          }
          if (msg?.type === "CLEAR") {
            this.clearLocalOnly();
          }
        } catch (err) {
          // ignore parse errors
        }
      }
    });
  }

  // Set token/sessionId/expiry -> write cookies + update memory + broadcast to other tabs
  setAuth(payload: AuthPayload) {
    if (payload.token) this.accessToken = payload.token;
    if (payload.sessionId) this.sessionId = payload.sessionId;
    if (payload.expiresAtUtc) this.expiresAtUtc = payload.expiresAtUtc;

    // Set cookie for token (non-HttpOnly fallback). Prefer server-set HttpOnly cookies.
    if (payload.token) {
      const expires = payload.expiresAtUtc
        ? new Date(payload.expiresAtUtc)
        : undefined;
      Cookies.set(COOKIE_TOKEN, payload.token!, {
        expires,
        secure: true,
        sameSite: "Lax",
        path: "/",
      });
    }

    // Set cookie for sessionId
    if (payload.sessionId) {
      const expires = payload.expiresAtUtc
        ? new Date(payload.expiresAtUtc)
        : undefined;
      Cookies.set(COOKIE_SESSION, payload.sessionId!, {
        expires,
        secure: true,
        sameSite: "Lax",
        path: "/",
      });
    }

    // Broadcast to other tabs
    const message = { type: "SET_TOKEN", payload };
    try {
      this.bc?.postMessage(message);
    } catch {
      // fallback using localStorage event
      localStorage.setItem("bbps-auth", JSON.stringify(message));
      // remove quickly to avoid piling storage keys
      setTimeout(() => localStorage.removeItem("bbps-auth"), 500);
    }
  }

  // get token (memory -> cookie fallback)
  getAccessToken() {
    return this.accessToken || Cookies.get(COOKIE_TOKEN) || null;
  }

  // get session id
  getSessionId() {
    return this.sessionId || Cookies.get(COOKIE_SESSION) || null;
  }

  // convenience setters
  setAccessToken(token?: string) {
    if (!token) return;
    this.setAuth({ token });
  }
  setSessionId(sessionId?: string) {
    if (!sessionId) return;
    this.setAuth({ sessionId });
  }
  setExpiry(expiresAtUtc?: string) {
    if (!expiresAtUtc) return;
    this.expiresAtUtc = expiresAtUtc;
  }

  // Remove tokens/cookies and broadcast CLEAR to other tabs
  clear() {
    this.clearLocalOnly();
    const message = { type: "CLEAR" };
    try {
      this.bc?.postMessage(message);
    } catch {
      localStorage.setItem("bbps-auth", JSON.stringify(message));
      setTimeout(() => localStorage.removeItem("bbps-auth"), 500);
    }
  }

  // clear only in-memory and cookies (used by clear and broadcast handler)
  private clearLocalOnly() {
    this.accessToken = null;
    this.sessionId = null;
    this.expiresAtUtc = null;
    Cookies.remove(COOKIE_TOKEN, { path: "/" });
    Cookies.remove(COOKIE_SESSION, { path: "/" });
  }

  // clear and redirect to login page
  clearAndRedirectToLogin() {
    this.clear();
    try {
      window.location.replace("/login");
    } catch (err) {
      console.error("Failed to redirect to login page", err);
    }
  }

  // Refresh token (single inflight refresh). Server endpoint must exist.
  async refreshToken(): Promise<void> {
    if (this.refreshingPromise) return this.refreshingPromise;
    const promise = (async () => {
      try {
        // server must issue a fresh token (ideally using httpOnly refresh cookie)
        const resp = await userHttp.post("/api/refresh"); // implement endpoint
        const payload = resp?.data?.data ?? resp?.data;
        if (!payload?.token) throw new Error("No token returned by refresh");
        this.setAuth({
          token: payload?.token,
          sessionId: payload?.sessionId,
          expiresAtUtc: payload?.expiresAtUtc,
        });
      } catch (err) {
        this.clearAndRedirectToLogin();
        throw err;
      } finally {
        this.refreshingPromise = null;
      }
    })();
    this.refreshingPromise = promise;
    return promise;
  }

  // basic validity check - returns true if a token exists; optionally decode expiry for stronger check
  hasValidToken(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    // OPTIONAL: decode JWT to check expiry (avoid if token is opaque)
    return true;
  }
}

// export singleton
export const SessionManager = new SessionManagerClass();
export default SessionManager;

// // Keep minimal secrets in memory. Use httpOnly cookies for refresh token.
// class _SessionManager {
//   private accessToken: string | null = null; // in-memory only
//   private userApiKey: string | null = null;
//   private csrfToken: string | null = null;
//   private refreshPromise: Promise<void> | null = null; // queue

//   getAccessToken() {
//     return this.accessToken
//       ? this.accessToken
//       : localStorage.getItem("bbps_token");
//   }
//   setAccessToken(token: string) {
//     this.accessToken = token;
//     localStorage.setItem("bbps_token", token);
//   }

//   getUserApiKey() {
//     return this.userApiKey;
//   }
//   setUserApiKey(k: string) {
//     this.userApiKey = k;
//   }

//   getCsrfToken() {
//     return this.csrfToken;
//   }
//   setCsrfToken(t: string) {
//     this.csrfToken = t;
//   }

//   createCorrelationId() {
//     // simple random id for tracing; replace with uuid lib if available
//     return `cid_${Math.random().toString(36).slice(2, 9)}`;
//   }

//   setSessionId(sessionId: string) {
//     localStorage.setItem("sessionId", sessionId);
//   }

//   getSessionId() {
//     return localStorage.getItem("sessionId");
//   }

//   setExpiry(expiry: string) {
//     localStorage.setItem("expiresAtUtc", expiry);
//   }

//   getExpiry() {
//     return localStorage.getItem("expiresAtUtc");
//   }

//   clear() {
//     localStorage.clear();
//   }

//   // refresh queue -> only one refresh call at a time
//   async refreshToken() {
//     if (this.refreshPromise) return this.refreshPromise;
//     this.refreshPromise = (async () => {
//       try {
//         // call refresh endpoint (relies on httpOnly refresh cookie)
//         const res = await fetch(
//           `${process.env.REACT_APP_USER_API_URL}/auth/refresh`,
//           { method: "POST", credentials: "include" },
//         );
//         if (!res.ok) throw new Error("Refresh failed");
//         const json = await res.json();
//         // server should return new access token + optionally new csrf or userApiKey
//         this.setAccessToken(json.accessToken);
//         if (json.csrf) this.setCsrfToken(json.csrf);
//         if (json.userApiKey) this.setUserApiKey(json.userApiKey);
//       } finally {
//         // clear queue when done
//         this.refreshPromise = null;
//       }
//     })();

//     return this.refreshPromise;
//   }

//   clearAndRedirectToLogin() {
//     this.accessToken = null;
//     this.userApiKey = null;
//     this.csrfToken = null;
//     // optionally clear raw store
//     window.location.href = "/login"; // or use router
//   }
// }

// export const SessionManager = new _SessionManager();
